/**
 * Mobile-only przycisk lupki + fullscreen modal z globalnym search'em.
 *
 * Pokazuje się TYLKO `< md` (`md:hidden`). Na desktopie (`md:block`) widoczny
 * jest inline `<GlobalSearch />` w Topbarze. Te dwa komponenty NIE rendererowane
 * jednocześnie — Topbar pokazuje oba (each ma własny media-query gate).
 *
 * Reuse: ten sam `useGlobalSearch` hook + ta sama backend logic. Inny tylko UI:
 * fullscreen modal z auto-focus na input + dropdown wyników natywnie pod inputem.
 */

import { cn } from '@/lib/cn';
import { LEAD_STATUS_LABELS, type LeadStatus } from '@coreltb/shared/constants';
import type { SearchLeadHit, SearchOfferHit } from '@coreltb/shared/types';
import { FileText, Search, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalSearch } from './api';

export function GlobalSearchMobile() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce 250ms (taki sam jak desktop GlobalSearch)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isFetching } = useGlobalSearch(debounced);

  // Sync open state z native dialog
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // Auto-focus input po showModal()
      window.setTimeout(() => inputRef.current?.focus(), 50);
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Native ESC close
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClose = () => {
      if (open) {
        setOpen(false);
        setQuery('');
        setDebounced('');
      }
    };
    dialog.addEventListener('close', onClose);
    return () => dialog.removeEventListener('close', onClose);
  }, [open]);

  const closeAndClear = () => {
    setOpen(false);
    setQuery('');
    setDebounced('');
  };

  // Backdrop click → close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) {
      closeAndClear();
    }
  };

  const hasQuery = debounced.trim().length >= 2;
  const leadsHits = data?.leads ?? [];
  const offersHits = data?.offers ?? [];
  const totalHits = leadsHits.length + offersHits.length;

  return (
    <>
      {/* Trigger — ikona lupki, widoczna tylko < md (desktop ma inline input) */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Szukaj"
        title="Szukaj"
        className="inline-flex h-touch w-touch items-center justify-center rounded-md text-fg-muted hover:bg-surface-2 hover:text-fg active:bg-surface-3 md:hidden"
      >
        <Search className="h-5 w-5" strokeWidth={2} />
      </button>

      {/* Fullscreen modal — szerokość 95vw, ogarnia ekran telefonu */}
      <dialog
        ref={dialogRef}
        onClick={handleBackdropClick}
        className="max-h-[90vh] w-full max-w-[95vw] rounded-lg border border-border bg-surface p-0 text-fg shadow-lg backdrop:bg-bg/80 backdrop:backdrop-blur-sm"
      >
        {/* Sticky header z input + close */}
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-surface px-3 py-2">
          <Search className="pointer-events-none h-4 w-4 shrink-0 text-fg-muted" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Szukaj wszędzie..."
            autoComplete="off"
            className="form-input flex-1 border-0 bg-transparent px-0 py-2 text-sm placeholder:text-fg-subtle focus:ring-0"
          />
          <button
            type="button"
            onClick={closeAndClear}
            className="rounded-md px-3 py-1.5 text-sm font-medium text-fg-muted hover:bg-surface-2 hover:text-fg active:bg-surface-3"
          >
            Anuluj
          </button>
        </div>

        {/* Wyniki */}
        <div className="max-h-[70vh] overflow-y-auto">
          {!hasQuery ? (
            <div className="px-4 py-8 text-center text-sm text-fg-muted">
              Wpisz min. 2 znaki.
              <div className="mt-2 text-xs text-fg-subtle">
                Szukamy po: mieście, regionie, tagach, telefonie, e-mailu, numerze oferty.
              </div>
            </div>
          ) : isFetching ? (
            <div className="px-4 py-8 text-center text-sm text-fg-muted">Szukam...</div>
          ) : totalHits === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-fg-muted">
              Brak wyników dla &laquo;{debounced}&raquo;.
            </div>
          ) : (
            <>
              {leadsHits.length > 0 && (
                <Group title="Leady" icon={User} count={leadsHits.length}>
                  {leadsHits.map((lead) => (
                    <LeadHit key={lead.id} lead={lead} onClose={closeAndClear} />
                  ))}
                </Group>
              )}
              {offersHits.length > 0 && (
                <Group title="Oferty" icon={FileText} count={offersHits.length}>
                  {offersHits.map((offer) => (
                    <OfferHit key={offer.id} offer={offer} onClose={closeAndClear} />
                  ))}
                </Group>
              )}
            </>
          )}
        </div>
      </dialog>
    </>
  );
}

function Group({
  title,
  icon: Icon,
  count,
  children,
}: {
  title: string;
  icon: import('lucide-react').LucideIcon;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 border-b border-border bg-bg/40 px-4 py-2 text-xs font-semibold text-fg-muted">
        <Icon className="h-3 w-3 text-accent" strokeWidth={2.5} />
        {title}
        <span className="ml-auto text-fg-subtle">{count}</span>
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function LeadHit({ lead, onClose }: { lead: SearchLeadHit; onClose: () => void }) {
  const statusLabel = LEAD_STATUS_LABELS[lead.status as LeadStatus] ?? lead.status;
  return (
    <li className="border-b border-border last:border-b-0">
      <Link
        to={`/crm/${lead.id}`}
        onClick={onClose}
        className="block px-4 py-3 transition-colors hover:bg-surface-2 active:bg-surface-3"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-fg">{lead.name ?? '—'}</span>
          <span className="shrink-0 rounded border border-border bg-bg/60 px-1.5 py-0.5 text-xs text-fg-muted">
            {statusLabel}
          </span>
        </div>
        <div className="mt-0.5 truncate text-xs text-fg-muted">
          {lead.city}
          {lead.phone && ` · ${lead.phone}`}
        </div>
      </Link>
    </li>
  );
}

function OfferHit({ offer, onClose }: { offer: SearchOfferHit; onClose: () => void }) {
  return (
    <li className="border-b border-border last:border-b-0">
      <Link
        to={`/oferty/${offer.id}`}
        onClick={onClose}
        className="block px-4 py-3 transition-colors hover:bg-surface-2 active:bg-surface-3"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-fg">
            {offer.offerNumberLabel ?? 'Oferta'}
            {offer.leadName && (
              <span className="ml-2 font-normal text-fg-muted">· {offer.leadName}</span>
            )}
          </span>
          <span
            className={cn(
              'shrink-0 rounded border px-1.5 py-0.5 text-xs',
              offer.status === 'accepted'
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : offer.status === 'sent' || offer.status === 'opened' || offer.status === 'clicked'
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300'
                  : 'border-border bg-bg/60 text-fg-muted',
            )}
          >
            {offer.status}
          </span>
        </div>
        <div className="mt-0.5 truncate text-xs text-fg-muted">
          {offer.leadCity} · {formatPln(offer.totalBruttoGrosze)} brutto
        </div>
      </Link>
    </li>
  );
}

function formatPln(grosze: number): string {
  return (grosze / 100).toLocaleString('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    maximumFractionDigits: 0,
  });
}
